import { Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  AttendeePreOrderStatus,
  CheckoutDeliveryStatusUpdatedEvent,
  CheckoutItem,
  CheckoutStatus,
  CheckoutStatusUpdatedEvent,
  CustomerPreOrderCheckout,
  OrderTicketItemStatusUpdatedEvent,
  PreOrderStatus,
} from '@ancon/wildcat-types'

import {
  NewOrderDetails,
  NewOrderPanelStep,
  NewOrderStep,
  PreOrderCheckoutEntityType,
  PreOrderReducerState,
  PreOrderStartStep,
  PreOrderUser,
} from '../types'
import { NewOrderPanelStepOrder } from '../constants'

import preOrderInitialState from './preOrderInitialState'

const preOrderReducers = {
  setPreOrderStartStep(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<PreOrderStartStep>,
  ) {
    state.startStep = action.payload
  },
  setPreOrderHostInfoModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.showPreOrderHostInfoModal = action.payload
  },
  setMobilePreOrderMembersManageStep(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isMobilePreOrderMembersManageStep = action.payload
  },
  setSelectedPreOrderAttendeeId(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<string | null>,
  ) {
    state.preOrderSelectedAttendeeId = action.payload
  },
  setPreOrderAttendeeRemoveModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isPreOrderAttendeeRemoveModalVisible = action.payload
  },
  setPreOrderUser(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<PreOrderUser | null>,
  ) {
    state.preOrderUser = action.payload
  },
  setPreOrderAttendanceConfirmationModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isAttendanceConfirmationModalVisible = action.payload
  },
  setPreOrderAttendeeEmptyItemsWarningModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isAttendeeEmptyItemsWarningModalVisible = action.payload
  },
  preOrderClearCheckout(state: Draft<PreOrderReducerState>) {
    state.preOrderCheckout = null
    state.preOrderLink = null
  },
  preOrderClearCheckoutSummary(state: Draft<PreOrderReducerState>) {
    state.preOrderCheckoutSummary = null
    state.preOrderCheckoutSummaryFetchPending = false
    state.preOrderSummaryOutletListItem = null
    state.preOrderCheckoutStatusLastUpdatedAt = 0
  },
  preOrderCheckoutEmitStatusUpdatedEvent(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<CheckoutStatusUpdatedEvent>,
  ) {
    function handleCheckoutStatusEvent(key: PreOrderCheckoutEntityType) {
      const {
        checkoutId,
        status,
        timestamp,
        serviceTime,
        cancelInfo,
        preparationTime,
        ticketId,
      } = action.payload

      if (state[key] && checkoutId === state[key]!.id) {
        let timestampDateValue = new Date(timestamp).valueOf()

        if (Number.isNaN(timestampDateValue)) {
          timestampDateValue = 0
        }

        if (
          !Number.isNaN(status) &&
          timestampDateValue >= state.preOrderCheckoutStatusLastUpdatedAt
        ) {
          state[key]!.checkoutStatus = status
          state.preOrderCheckoutStatusLastUpdatedAt = timestampDateValue

          if (
            status === CheckoutStatus.Captured ||
            status === CheckoutStatus.InProgress
          ) {
            // Update service time - otherwise there can be difference with POS and AOW
            state[key]!.serviceTime.time = serviceTime?.time || timestamp
          }
        }

        if (cancelInfo) {
          state[key]!.cancelInfo = cancelInfo
        }

        if (preparationTime) {
          state[key]!.tickets[0] = {
            ...state[key]!.tickets[0],
            preparationTime,
          }
        }

        if (ticketId) {
          state[key]!.tickets[0] = { ...state[key]!.tickets[0], id: ticketId }
        }
      }
    }

    handleCheckoutStatusEvent('preOrderCheckout')
    handleCheckoutStatusEvent('preOrderCheckoutSummary')
  },
  preOrderCheckoutEmitTicketItemStatusUpdatedEvent(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<OrderTicketItemStatusUpdatedEvent>,
  ) {
    const { orderId, ticketItemId, ticketItemStatus } = action.payload

    function updateTicketItemStatus(items?: Draft<CheckoutItem[]>) {
      items?.forEach(item => {
        const { ticketItem } = item
        if (
          ticketItem &&
          ticketItem.ticketItemId === ticketItemId &&
          ticketItem.status < ticketItemStatus
        ) {
          item.ticketItem!.status = ticketItemStatus
        }
      })
    }

    if (
      state.preOrderCheckoutSummary &&
      state.preOrderCheckoutSummary.orderId === orderId
    ) {
      updateTicketItemStatus(state.preOrderCheckoutSummary.items)
      updateTicketItemStatus(state.preOrderCheckoutSummary.attendee?.items)
      state.preOrderCheckoutSummary.attendees?.forEach(attendee => {
        updateTicketItemStatus(attendee.items)
      })
    }
  },
  preOrderCheckoutEmitDeliveryStatusUpdatedEvent(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<CheckoutDeliveryStatusUpdatedEvent>,
  ) {
    const { checkoutId, newDeliveryStatus } = action.payload
    const checkoutSummary = state.preOrderCheckoutSummary
    const deliveryStatus = checkoutSummary?.deliveryStatus ?? null

    if (
      checkoutSummary?.id === checkoutId &&
      (deliveryStatus === null || newDeliveryStatus > deliveryStatus)
    ) {
      checkoutSummary.deliveryStatus = newDeliveryStatus
    }
  },
  preOrderUpdateMembersStatus(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<{
      memberIds: string[]
      status: AttendeePreOrderStatus
    }>,
  ) {
    const { memberIds, status } = action.payload

    if (state.preOrderCheckout) {
      state.preOrderCheckout.attendees?.forEach(attendee => {
        if (memberIds.includes(attendee.id)) {
          attendee.status = status
        }
      })
    }
  },
  setOrderCalenderSelectedDate(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<string>,
  ) {
    state.orderCalendarSelectedDate = action.payload
  },
  setOrderCalendarSelectedMonth(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<string>,
  ) {
    state.orderCalendarSelectedMonth = action.payload
  },
  preOrderGoNextNewOrderStep(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<NewOrderStep>,
  ) {
    const nextStep = action.payload

    state.newOrderStep = nextStep
    if (nextStep === NewOrderStep.Summary) {
      state.newOrderEditModeStep = NewOrderStep.None
    }
  },
  preOrderGoBackNewOrderStep(state: Draft<PreOrderReducerState>) {
    const isEditMode = state.newOrderEditModeStep > NewOrderStep.None

    if (isEditMode) {
      state.newOrderStep = NewOrderStep.Summary
      state.newOrderEditModeStep = NewOrderStep.None
    } else {
      state.newOrderStep = Math.max(
        state.newOrderStep - 1,
        NewOrderStep.OrderConfiguration,
      )
    }
  },
  /**
   * Opens the new company order panel with fresh order details, e.g. with
   * the date picked in the overview, at its first step without a value
   */
  openNewOrderPanel(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<
      | {
          isDateFirst?: boolean
          details?: Partial<NewOrderDetails>
          currentStep?: NewOrderPanelStep
        }
      | undefined
    >,
  ) {
    const { isDateFirst = false, details, currentStep } = action.payload ?? {}
    const stepOrder = isDateFirst
      ? NewOrderPanelStepOrder.DateFirst
      : NewOrderPanelStepOrder.RestaurantFirst

    state.newOrder = { ...preOrderInitialState.newOrder, ...details }
    state.newOrderPanel = {
      isOpen: true,
      stepOrder,
      currentStep: currentStep ?? stepOrder[0],
      createdGroupOrderStatus: null,
    }
  },
  closeNewOrderPanel(state: Draft<PreOrderReducerState>) {
    state.newOrderPanel.isOpen = false
  },
  setNewOrderPanelStep(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<NewOrderPanelStep>,
  ) {
    state.newOrderPanel.currentStep = action.payload
  },
  /** A group order was created from the panel, show its confirmation */
  setNewOrderPanelCreatedGroupOrderStatus(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<PreOrderStatus | null>,
  ) {
    state.newOrderPanel.createdGroupOrderStatus = action.payload
    if (action.payload !== null) {
      state.newOrderPanel.isOpen = false
    }
  },
  setNewOrderDetails(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<Partial<NewOrderDetails>>,
  ) {
    state.newOrder = {
      ...state.newOrder,
      ...action.payload,
    }
  },
  resetNewOrderDetails(state: Draft<PreOrderReducerState>) {
    state.newOrder = preOrderInitialState.newOrder
    // If there is a created group order ID, reset it as well
    state.createdGroupOrderId = null
  },
  preOrderSetSelectedPreOrder(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<CustomerPreOrderCheckout | null>,
  ) {
    if (action.payload) {
      const selectedPreOrderListItem = action.payload
      state.selectedPreOrderId = selectedPreOrderListItem.id
      state.selectedPreOrderListItem = selectedPreOrderListItem
    } else {
      state.selectedPreOrderId = null
      state.selectedPreOrderListItem = null
      // Reset selected pre-order details
      state.selectedPreOrderDetails = null
    }
  },
  setNewPreOrderEditModeStep(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<NewOrderStep>,
  ) {
    state.newOrderEditModeStep = action.payload
  },
  setPreOrderLeaveModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isPreOrderLeaveModalVisible = action.payload
  },
  setPreOrderExistWarningModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isPreOrderExistWarningModalVisible = action.payload
  },
  setPreOrderTimeChangeModalVisible(
    state: Draft<PreOrderReducerState>,
    action: PayloadAction<boolean>,
  ) {
    state.isPreOrderTimeChangeModalVisible = action.payload
  },
  resetPreOrderState() {
    return preOrderInitialState
  },
}

export default preOrderReducers
